(() => {
  function normalizeSize(value) {
    const key = String(value || '').toUpperCase().replace(/^SIZE\s+/, '').replace(/[\s-]/g, '');
    return ({ SMALL: 'S', MEDIUM: 'M', LARGE: 'L', EXTRASMALL: 'XS', EXTRALARGE: 'XL', XXL: '2XL', XXXL: '3XL', XXS: '2XS' })[key] || key;
  }
  function nearestFrame(degrees, angle, complete) {
    let best = 0, distance = Infinity;
    degrees.forEach((value, index) => {
      const delta = Math.abs(value - angle);
      const gap = complete ? Math.min(delta, 360 - delta) : delta;
      if (gap < distance) { best = index; distance = gap; }
    });
    return best;
  }
  function snapAngle(angle, complete, threshold = 7) {
    if (!complete) return angle;
    const anchors = [0, 90, 180, 270, 360];
    const nearest = anchors.reduce((best, value) => Math.abs(value - angle) < Math.abs(best - angle) ? value : best, 0);
    return Math.abs(nearest - angle) <= threshold ? nearest : angle;
  }
  function stepFrame(index, delta, count, complete) {
    if (!count) return 0;
    const next = index + delta;
    return complete ? ((next % count) + count) % count : Math.max(0, Math.min(count - 1, next));
  }
  const SIZE_ORDER = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  // table: { height_cm: [165, 175, 185], weight_kg: [65, 75, ...], sizes: [[row per height band, one size per weight band]] }
  // A cell holds one size ("L") or two ("L/XL") when James marks that build as between sizes.
  // table.lead says which of the two is filled first: "smaller" (default) or "bigger".
  function suggestSize(table, heightCm, weightKg) {
    if (!(heightCm > 0 && weightKg > 0) || !table || !Array.isArray(table.sizes)) return null;
    if (heightCm < 120 || heightCm > 230 || weightKg < 35 || weightKg > 250) return { out: true };
    const row = (table.height_cm || []).filter(limit => heightCm >= limit).length;
    const column = (table.weight_kg || []).filter(limit => weightKg >= limit).length;
    const cell = table.sizes[row] && table.sizes[row][column];
    if (!cell) return { out: true };
    const sizes = String(cell).split('/').map(value => value.trim()).filter(Boolean);
    if (sizes.length > 1) return { size: table.lead === 'bigger' ? sizes[1] : sizes[0], pair: sizes.slice(0, 2), down: '', up: '' };
    const present = new Set(table.sizes.flat().flatMap(value => String(value).split('/')).map(normalizeSize));
    const order = SIZE_ORDER.filter(value => present.has(value));
    const index = order.indexOf(normalizeSize(sizes[0]));
    return { size: sizes[0], pair: null, down: order[index - 1] || '', up: order[index + 1] || '' };
  }
  // Body fat slider: 25%+ on the left to 5% on the right, with James's words for each band.
  const FAT_MAX = 25, FAT_MIN = 5;
  const FAT_WORDS = [[24, 'No abs'], [19, 'Almost abs'], [15, 'Decent abs'], [11, 'Shredded abs'], [7, 'Peeled glutes']];
  function fatWord(pct) { let word = FAT_WORDS[0][1]; FAT_WORDS.forEach(([from, name]) => { if (pct <= from) word = name; }); return word; }
  const fatLabel = pct => pct >= FAT_MAX ? `${FAT_MAX}%+` : `${pct}%`;
  // model: snippets/rd-size-model.liquid. Width comes from upper-body kg (lean + part of the fat) spread over height,
  // times frame; length from height; the bigger size wins. Lean people also get an off-season size.
  function modelSize(model, heightCm, weightKg, frame, fatPct) {
    if (!(heightCm > 0 && weightKg > 0) || !model || !model.width) return null;
    if (heightCm < 120 || heightCm > 230 || weightKg < 35 || weightKg > 250) return { out: true };
    const order = SIZE_ORDER.filter(size => size === 'S' || model.width[size] != null || (model.length || {})[size] != null);
    const sizeOn = (steps, value) => order.reduce((size, step) => steps && steps[step] != null && value >= steps[step] ? step : size, order[0]);
    const bigger = (a, b) => order.indexOf(a) >= order.indexOf(b) ? a : b;
    const set = fatPct != null;
    const pct = set ? fatPct : model.unset_pct;
    const lean = weightKg * (1 - pct / 100);
    const frameX = (model.frame || {})[frame || 'average'] || 1;
    const score = upper => 100 * Math.sqrt(upper / 175) * Math.pow(175 / heightCm, model.height_power) * frameX;
    const width = score(lean + model.fat_counts * (weightKg - lean));
    const lengthSize = sizeOn(model.length, heightCm);
    const size = bigger(sizeOn(model.width, width), lengthSize);
    const index = order.indexOf(size);
    if (set && fatPct <= model.off_season_at) {
      const offWeight = lean / (1 - model.off_season_pct / 100);
      const offSize = bigger(sizeOn(model.width, score(lean + model.fat_counts * (offWeight - lean))), lengthSize);
      return { size, offSize, pair: null, down: '', up: '' };
    }
    // Near a width step, offer both sizes and fill the bigger (the fit notes say size up).
    if (order.indexOf(lengthSize) < index && model.width[size] != null && width - model.width[size] <= model.between) return { size, pair: [order[index - 1], size], down: '', up: '' };
    const next = order[index + 1];
    if (next && model.width[next] != null && model.width[next] - width <= model.between && order.indexOf(lengthSize) <= index) return { size: next, pair: [size, next], down: '', up: '' };
    return { size, pair: null, down: order[index - 1] || '', up: order[index + 1] || '' };
  }
  function nearestModel(models, heightCm, weightKg) {
    let best = null;
    models.forEach(model => {
      if (!(model.height > 0 && model.weight > 0)) return;
      const distance = Math.abs(model.height - heightCm) / 8 + Math.abs(model.weight - weightKg) / 8;
      if (!best || distance < best.distance) best = { ...model, distance };
    });
    return best;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { normalizeSize, nearestFrame, stepFrame, snapAngle, suggestSize, modelSize, fatWord, nearestModel };
  if (typeof customElements === 'undefined' || customElements.get('rd-size-guide-v2')) return;

  customElements.define('rd-size-guide-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.model = 0; this.position = 0;
      this.panels = [...this.querySelectorAll('[data-model-panel]')];
      this.dialog = this.querySelector('dialog');
      this.slider = this.querySelector('[data-view-slider]');
      // Page product guides answer [data-size-guide-v2-open]; Full Stack component guides answer their handle's data-size-guide-target.
      this.key = this.dataset.guideKey || '';
      this.triggers = this.key ? [] : [...this.ownerDocument.querySelectorAll('[data-size-guide-v2-open]')];
      this.triggerFallbacks = this.triggers.map(trigger => ({ trigger, controls: trigger.getAttribute('aria-controls'), hidden: trigger.hidden }));
      this.triggers.forEach(trigger => { trigger.hidden = false; trigger.setAttribute('aria-controls', this.dialog.id); trigger.setAttribute('aria-expanded', 'false'); });
      this.ownerDocument.addEventListener('click', event => {
        const trigger = this.matchTrigger(event.target);
        if (trigger) { event.preventDefault(); event.stopImmediatePropagation(); this.openGuide(trigger); }
      }, { ...options, capture: true });
      this.initFinder(options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeGuide(); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeGuide();
      }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.slider?.addEventListener('input', () => { this.position = Number(this.slider.value); this.renderGallery(); }, options);
      this.slider?.addEventListener('pointerup', () => this.settleView(), options);
      const photos = this.querySelector('[data-photos]');
      photos.addEventListener('pointerdown', event => this.startDrag(event), options);
      photos.addEventListener('pointermove', event => this.moveDrag(event), options);
      photos.addEventListener('pointerup', () => { if (this.drag?.active) this.settleView(); this.drag = null; }, options);
      photos.addEventListener('pointercancel', () => { this.drag = null; }, options);
      // Touch pointers are captured to the touched frame on pointerdown; moving capture to the photo fires lostpointercapture on that frame, which must not end the drag.
      photos.addEventListener('lostpointercapture', event => { if (event.target === this.drag?.photo) this.drag = null; }, options);
      this.addEventListener('click', event => this.click(event), options);
      this.addEventListener('keydown', event => this.keydown(event), options);
      this.ownerDocument.defaultView.addEventListener('pagehide', () => this.closeGuide(), options);
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-model-panel]');
        if (panel) { this.openGuide(); this.model = Number(panel.dataset.modelPanel); this.position = 0; this.renderGallery(); }
      }, options);
      this.renderGallery();
    }
    disconnectedCallback() {
      this.closeGuide(); this.controller?.abort(); this.controller = null;
      this.triggerFallbacks?.forEach(({ trigger, controls, hidden }) => { trigger.hidden = hidden; if (controls) trigger.setAttribute('aria-controls', controls); else trigger.removeAttribute('aria-controls'); });
    }
    matchTrigger(node) {
      if (!this.key) return node.closest('[data-size-guide-v2-open]');
      const trigger = node.closest('[data-size-guide-target]');
      if (!trigger || trigger.getAttribute('data-size-guide-target') !== this.key) return null;
      const section = this.dataset.section && this.ownerDocument.getElementById('shopify-section-' + this.dataset.section);
      return !section || section.contains(trigger) ? trigger : null;
    }
    sizeButtons() {
      const trigger = this.openedBy;
      const scope = trigger?.closest('[data-rd3-selection]') || trigger?.closest('[data-bundle-product-id]') || trigger?.closest('.rd-pdp');
      return [...(scope?.querySelector('.rd-pdp__sizes')?.querySelectorAll('button') || [])];
    }
    openGuide(trigger) {
      if (this.dialog.open) return;
      this.openedBy = trigger || null;
      this.returnFocus = trigger || this.ownerDocument.activeElement;
      this.oldOverflow = this.ownerDocument.body.style.overflow;
      this.ownerDocument.body.style.overflow = 'hidden'; this.scrollLocked = true;
      this.cartSize = this.sizeButtons().find(button => button.getAttribute('aria-pressed') === 'true')?.textContent.trim() || '';
      const match = this.panels.findIndex(panel => this.cartSize && normalizeSize(panel.dataset.wornSize) === normalizeSize(this.cartSize));
      if (match >= 0) { this.model = match; this.position = 0; }
      this.loadFinder();
      this.dialog.showModal();
      this.renderGallery();
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.openedBy?.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-close]').focus({ preventScroll: true });
    }
    closeGuide() { if (this.dialog?.open) this.dialog.close(); this.afterClose(); }
    afterClose() {
      if (!this.scrollLocked) return;
      this.ownerDocument.body.style.overflow = this.oldOverflow;
      this.scrollLocked = false; this.drag = null;
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.openedBy?.setAttribute('aria-expanded', 'false');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
    frames() { return [...(this.panels[this.model]?.querySelectorAll('[data-frame]') || [])]; }
    complete() { return this.panels[this.model]?.dataset.completeRotation === 'true' && this.frames().length > 2; }
    frameIndex() {
      const frames = this.frames();
      return this.complete() ? nearestFrame(frames.map((_, index) => index * 360 / frames.length), this.position, true) : Math.round(this.position);
    }
    selectFrame(index) { this.position = this.complete() ? index * 360 / this.frames().length : index; this.renderGallery(); }
    startDrag(event) {
      if (!event.isPrimary || event.button !== 0 || !event.target.closest('[data-drag-views]')) return;
      const photo = event.target.closest('[data-drag-views]');
      this.drag = { x: event.clientX, y: event.clientY, pointer: event.pointerId, photo, position: this.position, width: Math.max(1, photo.getBoundingClientRect().width), active: false };
    }
    moveDrag(event) {
      const drag = this.drag;
      if (!drag || drag.pointer !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
        if (Math.abs(dx) < 8) return;
        drag.active = true; drag.photo.setPointerCapture(event.pointerId);
      }
      const complete = this.complete();
      const max = complete ? 360 : Math.max(0, this.frames().length - 1);
      const next = drag.position - dx / drag.width * max;
      this.position = complete ? ((next % 360) + 360) % 360 : Math.max(0, Math.min(max, next));
      this.renderGallery();
    }
    settleView() {
      this.position = this.complete() ? snapAngle(this.position, true) : Math.round(this.position);
      this.renderGallery();
    }
    click(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-close')) this.closeGuide();
      if (target.hasAttribute('data-model')) { this.model = Number(target.dataset.model); this.position = 0; this.renderGallery(); }
      if (target.hasAttribute('data-step')) this.selectFrame(stepFrame(this.frameIndex(), Number(target.dataset.step), this.frames().length, this.complete()));
      if (target.hasAttribute('data-angle')) { this.position = Number(target.dataset.angle); this.renderGallery(); }
      if (target.dataset.unitH) this.setUnit('h', target.dataset.unitH);
      if (target.dataset.unitW) this.setUnit('w', target.dataset.unitW);
      if (target.hasAttribute('data-finder-go')) this.chooseSize(this.suggestion);
      if (target.hasAttribute('data-finder-size')) this.chooseSize(target.dataset.size);
      if (target.hasAttribute('data-finder-model')) this.showModel(Number(target.dataset.modelIndex));
      if (target.hasAttribute('data-finder-more')) { this.moreOpen = !this.moreOpen; this.renderBuild(); }
      if (target.dataset.frame) { this.frame = this.frame === target.dataset.frame ? '' : target.dataset.frame; this.saveFinder(); this.renderFinder(true); }
      if (target.hasAttribute('data-fat-clear')) { this.fat = null; this.saveFinder(); this.renderFinder(true); }
    }
    keydown(event) {
      if (event.key === 'Tab' && this.dialog.open) {
        const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); return; }
        if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); return; }
      }
      if (event.target === this.slider && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) {
        event.preventDefault();
        const count = this.frames().length;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : stepFrame(this.frameIndex(), ['ArrowRight','ArrowUp'].includes(event.key) ? 1 : -1, count, this.complete());
        this.selectFrame(next);
      }
    }
    initFinder(options) {
      this.finder = this.querySelector('[data-finder]');
      if (!this.finder) return;
      try { this.table = JSON.parse(this.finder.querySelector('[data-finder-table]').textContent); } catch (error) { this.table = null; }
      try { this.sizeModel = JSON.parse(this.finder.querySelector('[data-finder-size-model]')?.textContent || 'null'); } catch (error) { this.sizeModel = null; }
      this.frame = ''; this.fat = null; this.moreOpen = false;
      this.models = this.panels.map((panel, index) => ({ index, name: panel.dataset.modelName, size: panel.dataset.wornSize, height: parseFloat(panel.dataset.height), weight: parseFloat(panel.dataset.weight) }));
      this.units = { h: 'cm', w: 'kg' }; this.heightCm = 0; this.weightKg = 0;
      this.finder.addEventListener('input', event => this.readInput(event.target), options);
    }
    loadFinder() {
      if (!this.finder) return;
      let saved = {};
      try { saved = JSON.parse(this.ownerDocument.defaultView.localStorage.getItem('rd-size-finder') || '{}') || {}; } catch (error) { saved = {}; }
      this.units = { h: saved.hu === 'ft' ? 'ft' : 'cm', w: saved.wu === 'lb' ? 'lb' : 'kg' };
      this.heightCm = Number(saved.h) || 0; this.weightKg = Number(saved.w) || 0;
      this.frame = ['narrow', 'average', 'broad'].includes(saved.fr) ? saved.fr : '';
      this.fat = Number.isFinite(saved.bf) && saved.bf >= FAT_MIN && saved.bf <= FAT_MAX ? saved.bf : null;
      this.moreOpen = !!(this.frame || this.fat != null);
      this.fillInputs(); this.renderFinder(true);
    }
    saveFinder() {
      try { this.ownerDocument.defaultView.localStorage.setItem('rd-size-finder', JSON.stringify({ h: this.heightCm, w: this.weightKg, hu: this.units.h, wu: this.units.w, fr: this.frame || undefined, bf: this.fat ?? undefined })); } catch (error) { /* storage unavailable */ }
    }
    input(name) { return this.finder.querySelector(`[data-in="${name}"]`); }
    fillInputs() {
      const inches = this.heightCm ? Math.round(this.heightCm / 2.54) : 0;
      this.input('cm').value = this.heightCm ? Math.round(this.heightCm) : '';
      this.input('ft').value = inches ? Math.floor(inches / 12) : '';
      this.input('in').value = inches ? inches % 12 : '';
      this.input('kg').value = this.weightKg ? Math.round(this.weightKg) : '';
      this.input('lb').value = this.weightKg ? Math.round(this.weightKg * 2.20462) : '';
      ['h', 'w'].forEach(axis => {
        this.finder.querySelectorAll(`[data-when-${axis}]`).forEach(node => { node.hidden = node.getAttribute(`data-when-${axis}`) !== this.units[axis]; });
        this.finder.querySelectorAll(`[data-unit-${axis}]`).forEach(button => button.setAttribute('aria-pressed', button.getAttribute(`data-unit-${axis}`) === this.units[axis]));
      });
    }
    readInput(field) {
      if (field.hasAttribute('data-fat')) { this.fat = FAT_MAX - Number(field.value); this.saveFinder(); this.renderFinder(true); return; }
      const name = field.dataset.in;
      if (!name) return;
      const value = name => parseFloat(this.input(name).value) || 0;
      if (name === 'cm') this.heightCm = value('cm');
      if (name === 'ft' || name === 'in') this.heightCm = value('ft') ? (value('ft') * 12 + value('in')) * 2.54 : 0;
      if (name === 'kg') this.weightKg = value('kg');
      if (name === 'lb') this.weightKg = value('lb') / 2.20462;
      this.saveFinder(); this.renderFinder(true);
    }
    setUnit(axis, unit) {
      this.units[axis] = unit;
      this.fillInputs(); this.saveFinder();
      this.finder.querySelector(`[data-when-${axis}="${unit}"] input`)?.focus({ preventScroll: true });
    }
    renderFinder(followModel) {
      if (!this.finder) return;
      const result = this.sizeModel ? modelSize(this.sizeModel, this.heightCm, this.weightKg, this.frame, this.fat) : suggestSize(this.table, this.heightCm, this.weightKg);
      this.renderBuild();
      const go = this.finder.querySelector('[data-finder-go]'), title = this.finder.querySelector('[data-finder-title]'), sub = this.finder.querySelector('[data-finder-sub]');
      const row = this.querySelector('[data-finder-model]');
      this.suggestion = result && !result.out ? result.size : '';
      const offSeason = this.suggestion && result.offSize && normalizeSize(result.offSize) !== normalizeSize(result.size);
      const pair = this.suggestion ? (offSeason ? [result.size, result.offSize] : result.pair) : null;
      const between = this.finder.querySelector('[data-finder-between]');
      go.hidden = !!pair; between.hidden = !pair;
      if (pair) {
        between.querySelector('[data-between-title]').textContent = offSeason ? `Now ${pair[0]}` : `Between ${pair[0]} and ${pair[1]}`;
        between.querySelector('[data-between-sub]').textContent = offSeason ? `${pair[1]} for off-season` : `${pair[0]} closer · ${pair[1]} roomier`;
        between.querySelectorAll('[data-finder-size]').forEach((button, index) => { button.textContent = pair[index]; button.dataset.size = pair[index]; button.setAttribute('aria-label', `Select ${pair[index]}`); button.classList.toggle('is-main', normalizeSize(pair[index]) === normalizeSize(result.size)); });
      }
      go.disabled = !this.suggestion;
      title.textContent = this.suggestion ? `Go with ${this.suggestion}` : 'Your size';
      sub.textContent = !result ? sub.dataset.empty : result.out ? sub.dataset.out : result.offSize ? 'Fits off-season too' : [result.down && `${result.down} closer`, result.up && `${result.up} roomier`].filter(Boolean).join(' · ');
      const model = this.suggestion && nearestModel(this.models, this.heightCm, this.weightKg);
      row.hidden = !model;
      if (model) {
        row.dataset.modelIndex = model.index;
        row.querySelector('[data-finder-model-name]').textContent = `${model.name} · ${Math.round(model.height)}cm · ${Math.round(model.weight)}kg`;
        row.querySelector('[data-finder-model-size]').textContent = `Wears ${model.size} \u2192`;
        row.setAttribute('aria-label', `See ${model.name}, ${Math.round(model.height)} centimetres and ${Math.round(model.weight)} kilograms, wearing ${model.size}`);
      }
      // Show the closest build's photos while someone is sizing themselves.
      if (model && followModel && model.index !== this.model) { this.model = model.index; this.position = 0; this.renderGallery(); }
    }
    renderBuild() {
      const build = this.finder?.querySelector('[data-finder-build]');
      if (!build) return;
      const more = this.finder.querySelector('[data-finder-more]');
      build.hidden = !this.moreOpen;
      more.setAttribute('aria-expanded', this.moreOpen);
      more.querySelector('[data-more-sign]').textContent = this.moreOpen ? '\u2212' : '+';
      const answered = [this.frame && this.frame[0].toUpperCase() + this.frame.slice(1), this.fat != null && fatLabel(this.fat)].filter(Boolean);
      more.querySelector('[data-more-state]').textContent = !this.moreOpen && answered.length ? answered.join(' \u00b7 ') : 'Optional';
      build.querySelectorAll('[data-frame]').forEach(button => button.setAttribute('aria-pressed', button.dataset.frame === this.frame));
      const slider = build.querySelector('[data-fat]'), set = this.fat != null;
      const pct = set ? this.fat : this.sizeModel.unset_pct;
      slider.value = FAT_MAX - pct;
      slider.style.setProperty('--position', `${(FAT_MAX - pct) / (FAT_MAX - FAT_MIN) * 100}%`);
      slider.classList.toggle('is-unset', !set);
      slider.setAttribute('aria-valuetext', set ? `${fatLabel(pct)}, ${fatWord(pct)}` : 'Not set');
      build.querySelector('[data-fat-pct]').textContent = set ? fatLabel(pct) : '';
      build.querySelector('[data-fat-word]').textContent = set ? fatWord(pct) : 'Slide to set';
      build.querySelector('[data-fat-clear]').hidden = !set;
    }
    chooseSize(size) {
      if (!size) return;
      const button = this.sizeButtons().find(candidate => normalizeSize(candidate.textContent) === normalizeSize(size));
      if (button?.disabled) { this.finder.querySelectorAll('[data-finder-sub], [data-between-sub]').forEach(node => { node.textContent = `${size} is sold out`; }); return; }
      if (button && button.getAttribute('aria-pressed') !== 'true') button.click();
      this.closeGuide();
    }
    showModel(index) {
      if (!(index >= 0)) return;
      this.model = index; this.position = 0; this.renderGallery();
      const models = this.querySelector('.rd-sgv2__models'), scroll = this.querySelector('.rd-sgv2__scroll');
      const reduce = this.ownerDocument.defaultView.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (models && scroll) scroll.scrollTo({ top: models.offsetTop - 8, behavior: reduce ? 'auto' : 'smooth' });
    }
    loadNearbyFrames(frames, selected, complete) {
      if (!this.dialog.open) return;
      const indices = [selected, stepFrame(selected, -1, frames.length, complete), stepFrame(selected, 1, frames.length, complete)];
      [...new Set(indices)].forEach(index => {
        const image = frames[index]?.querySelector('img[data-photo-src]');
        if (!image || image.hasAttribute('src')) return;
        // Load only the visible image and immediate neighbours, including the rotation seam.
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = index === selected ? 'auto' : 'low';
        image.src = image.dataset.photoSrc;
      });
    }
    renderGallery() {
      if (!this.slider) return;
      const context = this.querySelector('[data-photo-context]');
      const wornSize = this.panels[this.model]?.dataset.wornSize || '';
      const mismatch = this.cartSize && normalizeSize(this.cartSize) !== normalizeSize(wornSize);
      const hasMatch = this.panels.some(panel => normalizeSize(panel.dataset.wornSize) === normalizeSize(this.cartSize));
      context.hidden = !mismatch;
      context.textContent = mismatch ? `${hasMatch ? `Size ${this.cartSize} selected for cart.` : `No model photos in ${this.cartSize}.`} Showing ${wornSize}.` : '';
      const frames = this.frames(), complete = this.complete(), selected = this.frameIndex();
      this.loadNearbyFrames(frames, selected, complete);
      this.panels.forEach((panel, index) => { panel.hidden = index !== this.model; });
      frames.forEach((frame, index) => { frame.hidden = index !== selected; });
      this.querySelectorAll('[data-model]').forEach(button => button.setAttribute('aria-pressed', Number(button.dataset.model) === this.model));
      this.slider.max = complete ? 360 : Math.max(1, frames.length - 1);
      this.slider.step = complete ? .1 : 1; this.slider.value = this.position;
      this.querySelector('[data-gallery-controls]').hidden = frames.length < 2;
      this.querySelector('[data-rotation-controls]').hidden = !complete;
      const label = `${selected + 1} / ${frames.length}`;
      this.querySelector('[data-photo-count]').textContent = label;
      this.slider.setAttribute('aria-valuetext', `Photo ${selected + 1} of ${frames.length}${complete ? `, ${Math.round(selected * 360 / frames.length)} degrees` : ''}`);
      this.slider.style.setProperty('--position', `${this.position / Number(this.slider.max) * 100}%`);
      this.querySelectorAll('[data-step]').forEach(button => { button.disabled = !complete && (Number(button.dataset.step) < 0 ? selected === 0 : selected === frames.length - 1); });
      this.querySelectorAll('[data-angle]').forEach(button => button.setAttribute('aria-pressed', Number(button.dataset.angle) === this.position));
    }
  });
})();
